import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import { inputVariants } from "@react/shared/ui/Input"
import { t } from "../helpers/format"

const Spin = ({ on, icon }) => <FontAwesomeIcon icon={on ? "circle-notch" : icon} spin={on} />

const DrawerFooter = ({ day, busy, onApprove, onReject, onReopen }) => {
  const [rejecting, setRejecting] = useState(false)
  const [reason,    setReason]    = useState("")
  const field = useRef(null)

  useEffect(() => { setRejecting(false); setReason("") }, [day.user?.id, day.date])
  useEffect(() => { if (rejecting) field.current?.focus() }, [rejecting])

  const can = day.can || {}

  if (day.closed) {
    return (
      <footer className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2 text-[12.5px] text-slate-600">
        <FontAwesomeIcon icon="lock" className="text-slate-400" />
        {t("drawer.closed_note")}
      </footer>
    )
  }

  if (rejecting) {
    const submit = () => onReject(reason.trim()).then(() => setRejecting(false)).catch(() => {})
    return (
      <footer className="px-6 py-4 border-t border-rose-200 bg-rose-50/60 flex flex-col gap-3 animate-[hb-rise_160ms_ease-out]">
        <label className="flex flex-col gap-1.5 m-0">
          <span className="text-[12px] font-semibold text-rose-700">{t("reject.label")}</span>
          <textarea
            ref         = {field}
            rows        = {3}
            value       = {reason}
            placeholder = {t("reject.placeholder")}
            onChange    = {e => setReason(e.target.value)}
            onKeyDown   = {e => {
              if (e.key === "Escape") { e.preventDefault(); setRejecting(false) }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && reason.trim()) submit()
            }}
            className   = {`${inputVariants({ size: "sm" })} !h-auto py-1.5 resize-none`}
          />
        </label>
        <div className="flex items-center justify-between">
          <Button variant="secondary" onClick={() => setRejecting(false)}>{t("actions.cancel")}</Button>
          <Button variant="danger" disabled={!reason.trim() || busy === "reject"} onClick={submit}>
            <Spin on={busy === "reject"} icon="rotate-left" />
            {t("reject.submit")}
          </Button>
        </div>
      </footer>
    )
  }

  if (!can.approve && !can.reopen) {
    return (
      <footer className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2 text-[12.5px] text-slate-500">
        <FontAwesomeIcon icon="eye" className="text-slate-400" />
        {day.status === "approved" ? t("drawer.approved_note") : t("drawer.read_only")}
      </footer>
    )
  }

  return (
    <footer className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between gap-3">
      <div>
        {can.approve && (
          <Button variant="danger-ghost" disabled={!!busy} onClick={() => setRejecting(true)}>
            <FontAwesomeIcon icon="rotate-left" />
            {t("actions.reject")}
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {can.reopen && (
          <Button variant="secondary" disabled={!!busy} onClick={onReopen}>
            <Spin on={busy === "reopen"} icon="lock-open" />
            {t("actions.reopen")}
          </Button>
        )}
        {can.approve && (
          <Button variant="primary" className="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700 focus:ring-emerald-500/30" disabled={!!busy} onClick={onApprove}>
            <Spin on={busy === "approve"} icon="check" />
            {t("actions.approve_day")}
          </Button>
        )}
      </div>
    </footer>
  )
}

export default DrawerFooter
